
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, User, Phone, Mail, CalendarDays, StickyNote } from "lucide-react";
import { Lead } from "@/types";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import LeadCallFlow from "@/components/leads/LeadCallFlow";
import { fetchAvailableVoices } from "@/services/campaigns/voiceService";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { formatDistanceToNow, format } from "date-fns";

const LeadCallFlowPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [selectedVoiceId, setSelectedVoiceId] = useState<string>("");
  
  const { data: lead, isLoading: isLeadLoading, error: leadError } = useQuery({
    queryKey: ["lead", id],
    queryFn: async () => {
      if (!id) throw new Error("Lead ID is required");
      
      const { data, error } = await supabase
        .from("leads")
        .select("*")
        .eq("id", id)
        .single();
      
      if (error) throw error;
      return data as Lead;
    },
    enabled: !!id
  });
  
  const { data: voicesData, isLoading: isVoicesLoading } = useQuery({
    queryKey: ["voices"],
    queryFn: async () => {
      const result = await fetchAvailableVoices();
      if (!result.success) throw new Error(result.error);
      return result.voices || [];
    }
  });
  
  useEffect(() => {
    if (voicesData && voicesData.length > 0 && !selectedVoiceId) {
      // Set the first voice as default
      setSelectedVoiceId(voicesData[0].voice_id);
    }
  }, [voicesData, selectedVoiceId]);
  
  const handleVoiceChange = (value: string) => {
    setSelectedVoiceId(value);
  };
  
  const handleCallComplete = () => {
    toast({
      title: "Call Flow Complete",
      description: "The lead has been processed through the AI call flow"
    });
  };
  
  if (leadError) {
    return (
      <div className="container max-w-4xl mx-auto py-8">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle>Error Loading Lead</CardTitle>
            <CardDescription>
              Could not load lead details: {leadError instanceof Error ? leadError.message : "Unknown error"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate(-1)}>
              <ArrowLeft className="mr-2 h-4 w-4" /> Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="container max-w-4xl mx-auto py-8">
      <div className="mb-6">
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Leads
        </Button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Lead details sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Lead Details</CardTitle>
              <CardDescription>
                Contact information and notes
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLeadLoading ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                </div>
              ) : lead ? (
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <User className="h-4 w-4 mt-0.5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{lead.name}</p>
                      <p className="text-xs text-muted-foreground">Name</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Phone className="h-4 w-4 mt-0.5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{lead.phone}</p>
                      <p className="text-xs text-muted-foreground">Phone</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Mail className="h-4 w-4 mt-0.5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{lead.email}</p>
                      <p className="text-xs text-muted-foreground">Email</p>
                    </div>
                  </div>
                  
                  {lead.created_at && (
                    <div className="flex items-start gap-3">
                      <CalendarDays className="h-4 w-4 mt-0.5 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{format(new Date(lead.created_at), "MMM d, yyyy")}</p>
                        <p className="text-xs text-muted-foreground">
                          Added {formatDistanceToNow(new Date(lead.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {lead.meta_notes && (
                    <div className="flex items-start gap-3">
                      <StickyNote className="h-4 w-4 mt-0.5 text-muted-foreground" />
                      <div>
                        <p className="font-medium">Notes</p>
                        <p className="text-sm">{lead.meta_notes}</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground">No lead data available</p>
              )}
            </CardContent>
          </Card>
          
          {lead && (
            <Card>
              <CardHeader>
                <CardTitle>Voice Selection</CardTitle>
                <CardDescription>
                  Choose a voice for the call
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Select value={selectedVoiceId} onValueChange={handleVoiceChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a voice" />
                  </SelectTrigger>
                  <SelectContent>
                    {isVoicesLoading ? (
                      <div className="p-2 text-center">Loading voices...</div>
                    ) : voicesData && voicesData.length > 0 ? (
                      voicesData.map((voice) => (
                        <SelectItem key={voice.voice_id} value={voice.voice_id}>
                          {voice.name}
                        </SelectItem>
                      ))
                    ) : (
                      <div className="p-2 text-center">No voices available</div>
                    )}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>
          )}
        </div>
        
        {/* Call flow main content */}
        <div className="md:col-span-2">
          {lead ? (
            <LeadCallFlow 
              lead={lead} 
              voiceId={selectedVoiceId}
              onCallComplete={handleCallComplete}
            />
          ) : isLeadLoading ? (
            <Card>
              <CardContent className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Lead Not Found</CardTitle>
                <CardDescription>
                  The requested lead could not be found
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={() => navigate(-1)}>
                  <ArrowLeft className="mr-2 h-4 w-4" /> Go Back
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadCallFlowPage;
